import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import type { TrainingEventCard, TrainingGroupResponse, TrainingTrainerOption } from '@bm/contracts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { extractTrainingEventId } from '../lib/training-event-slug';
import { EditEventModal, TrainingRespondModal, type TrainingEventRow } from './TrainingPage';

/** Eigene Seite pro Training (§ Nutzeranfrage „jedes Training eine separate
 * Seite bzw. ID … Trainingsgruppe und Datum … enthalten … auch als Pop-up
 * anzeigen"), Route `/training/events/$slug` — „Variante eins" aus der
 * Planung: die Seite zeigt immer das volle `TrainingMenuModal` (wie
 * `MemberDetailPage.tsx` der einzige bisherige Präzedenzfall für eine
 * Detailseite-pro-Entität in dieser App), inzwischen **der einzige**
 * Aufrufer von `TrainingMenuModal` — `EventCard` auf `TrainingPage.tsx`
 * navigiert seit § Nutzerfeedback „Adresszeile … den entsprechenden Link
 * haben" ebenfalls hierher, statt ein eigenes, dupliziertes Menü lokal zu
 * halten (siehe docs/training.md). Damit beim Navigieren dorthin keine
 * Funktion verloren geht, bietet diese Seite — anders als
 * `CalendarPage.tsx`s schlankeres Klick-to-Respond-Pop-up — auch
 * Bearbeiten/Löschen an, inklusive eigenem `EditEventModal`-Aufruf (von
 * `TrainingPage.tsx` exportiert, lädt/invalidiert seine Daten selbst über
 * `useQueryClient()`, daher ohne Anpassung hier wiederverwendbar).
 * Schließen (`onClose`) navigiert zurück zur Trainingsliste. `$slug` ist
 * nur der lesbare Teil der URL; der echte Schlüssel ist die UUID am Ende
 * (`extractTrainingEventId()`, siehe `training-event-slug.ts`) — alles
 * davor wird beim Öffnen ignoriert. */
export function TrainingEventPage() {
  const { slug } = useParams({ strict: false });
  const navigate = useNavigate();
  const { can } = useAuth();
  const eventId = slug ? extractTrainingEventId(slug) : null;
  const [editing, setEditing] = useState(false);

  // Dieselbe Karte, die `TrainingRespondModal` ohnehin lädt (gleicher
  // Query-Key, geteilter Cache) — nur um `seriesId` für die Lösch-Nachfrage
  // zu kennen, ohne die Logik aus `TrainingRespondModal` zu kopieren.
  const { data: card } = useQuery({
    queryKey: ['training', 'card', eventId],
    queryFn: () => api<TrainingEventCard>(`/training/events/${eventId}/card`, { orgScoped: true }),
    enabled: !!eventId,
  });
  const { data: groups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
    enabled: editing,
  });
  const { data: trainers } = useQuery({
    queryKey: ['training', 'trainers'],
    queryFn: () => api<TrainingTrainerOption[]>('/training/trainers', { orgScoped: true }),
    enabled: editing,
  });
  const { data: editingEvent } = useQuery({
    queryKey: ['training', 'events', eventId, 'detail'],
    queryFn: () => api<TrainingEventRow>(`/training/events/${eventId}`, { orgScoped: true }),
    enabled: editing,
  });

  const removeEvent = useMutation({
    mutationFn: () => api(`/training/events/${eventId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void navigate({ to: '/training' }),
  });

  function handleDelete() {
    if (!card) return;
    const msg = card.seriesId
      ? 'Diesen einen Termin aus der Serie löschen? Die übrigen Termine der Serie bleiben bestehen.'
      : 'Diesen Termin wirklich löschen?';
    if (confirm(msg)) removeEvent.mutate();
  }

  if (!eventId) {
    return <p className="text-sm text-slate-500">Ungültiger Link zu diesem Training.</p>;
  }

  return (
    <>
      <TrainingRespondModal
        eventId={eventId}
        onClose={() => void navigate({ to: '/training' })}
        onEdit={can('training.edit') ? () => setEditing(true) : undefined}
        onDelete={can('training.delete') ? handleDelete : undefined}
        deletePending={removeEvent.isPending}
      />
      {editing && editingEvent && (
        <EditEventModal event={editingEvent} groups={groups} trainers={trainers} onClose={() => setEditing(false)} />
      )}
    </>
  );
}
